import { intercept, observe } from "mobx";

/**
 * End every service call that was started in a session that has since been
 * signed out.
 *
 * `RootStore.purgeUserData` empties every store on sign-out, but a call already
 * in flight at that moment still resolves afterwards, and the code that issued
 * it carries on as if nothing happened: a list refills the store the purge just
 * emptied, a one-time API key is written back into a cache and reopens its
 * reveal dialog, a loader latches its `loaded` flag over an empty cache, a poll
 * starts its timer again.
 *
 * This is one application-level Feathers hook, so it covers every service call
 * the web app makes — library ServiceStores, raw view-store calls, custom
 * methods (`generatePlan`, `pullUsage`, …), create-as-read calls, and stores
 * written later — without each caller having to remember. It records the
 * session epoch (`RootStore.sessionEpoch`, bumped by purgeUserData) when a call
 * starts, and if the epoch has moved by the time the call settles, the CLIENT
 * promise REJECTS with {@link SessionEndedError} — whatever the call returned
 * or threw, and whatever its method.
 *
 * Never an empty result. The previous design resolved a stale `find` with
 * `[]`: wrong for a `get`, wrong for a find whose answer is one composite
 * object, and it let every continuation behind the await carry on and latch
 * "loaded" over a cache the purge had emptied (or, for a kept store with
 * `replaceOnFind`, empty it). A rejection is the one answer no caller mistakes
 * for data. What a caller does with it:
 *
 *  - a library ServiceStore (`throwOnError` false) catches it, records it on
 *    `errorOn*`, leaves its rows alone, and hands its caller `undefined`;
 *  - a view store's own `try/catch` takes its error path; the ones that would
 *    otherwise write state, latch a flag or start a timer check
 *    `SessionEndedError`/`isSessionEnded` there (see the call sites that import
 *    it).
 *
 * WRITES are covered too, and the server still performed them. A create/patch/
 * update/remove (or custom method) started under the old session reached the
 * server; only the client's application of its answer is dropped, so a
 * plaintext key from `api-keys.create` never reaches a store or a dialog after
 * the purge. (The record exists server-side; the next sign-in can revoke it.)
 * This cannot double-submit, for two reasons that are pinned by tests:
 *
 *  1. The library's online `create/patch/remove` falls back to its offline
 *     queue when the failure "carries no server decision" (no numeric code, or
 *     a transient one — `@intrasync/stores/offline` errorClass). The error
 *     carries `code` 499, which that classifier treats as a final server
 *     decision, so a write rejected here is never queued for replay.
 *  2. The queue's own replay (`OfflineServiceStore.syncWithServer`, including
 *     the pre-logout flush) is the one thing that must NOT be rejected: a
 *     rejected replay would be charged a retry and sent again. `isReplay`
 *     (offlineReplay.ts) recognises it as exactly the call of the op the queue
 *     is executing right now — the op's own `data` object for a create/patch,
 *     its entity for a remove — and exempts it, once.
 *
 * The `authentication` and `refresh-token` services belong to the sign-in and
 * sign-out themselves, so their reads, revokes and logouts are never touched.
 * Their `create` (a sign-in, a token refresh, `app.authenticate`) gets one
 * narrower rule, on its own clock (`authEpoch`, bumped synchronously when a
 * sign-out STARTS — on every path, see {@link watchSessionEnds} — and again
 * when it purges): a `create` started under one and settling under another
 * ends with {@link AuthSupersededError}. Without it a refresh begun by the
 * person signing out resolves afterwards and the library adopts THEIR rotated
 * tokens and user over the next person's session. The error carries a
 * transient code (408) on purpose: `AuthStore.isAuthRejection` treats it as "try
 * again later, keep your tokens" — a 4xx like 499 would be read as a rejected
 * credential and sign the NEW person out.
 *
 * A refresh (`refresh-token.create`, and the jwt `authentication.create` hop
 * that follows it) gets a second, cross-tab check on top: a compare-and-swap
 * against the refresh token in the storage every tab shares. The epoch only
 * moves when THIS tab hears of a sign-out, and a sibling's `storage` event can
 * arrive after this tab's refresh has already answered — the refresh would then
 * write the previous person's rotated tokens over the next person's, and every
 * other tab would adopt them. So the value in shared storage is read when the
 * refresh starts and again when it settles; if another tab cleared or replaced
 * it in between, the refresh is superseded (the same transient 408) and this
 * tab neither adopts nor writes back what it got. A same-person rotation by a
 * sibling ends the same way, harmlessly: this tab has already adopted the
 * sibling's token, and its next refresh spends that one.
 *
 * The CAS only proves that storage did not CHANGE during the call; it cannot
 * tell that the credential the call presents is the one storage already held
 * when the call began. A stale retry started AFTER another tab replaced the
 * session (that tab's `storage` event not yet delivered here) would otherwise
 * record the new person's token as its baseline and sail through. So a refresh
 * is also bound to the credential it spends, before it is sent: the
 * `refresh-token.create` must present a refresh token of the person storage's
 * refresh token belongs to (compared by `sub`, so a legitimate rotation that
 * storage could not take does not read as another person's), and the
 * jwt `authentication.create` must present an access token whose `sub` is the
 * stored refresh token's (or, with no refresh token stored, the stored access
 * token's). Either mismatch ends in the same transient 408, unsent.
 *
 * An offline replay is classified by the caller (`isReplay`): `true` lets it
 * through, `"refuse"` throws {@link ReplayHeldError} (transient too: the queue
 * keeps the op, charges nothing and stops the pass), `false` is an ordinary call.
 */

/** The parts of a Feathers hook context this reads. */
export type GuardContext = {
  path: string;
  method: string;
  id?: unknown;
  data?: unknown;
  result?: unknown;
};

type AroundHook = (context: GuardContext, next: () => Promise<void>) => Promise<void>;

/** What the offline queue's replay check says about a call (see offlineReplay.ts). */
export type ReplayVerdict = boolean | "refuse";

/**
 * What the host lends the guard beyond the epochs. All optional.
 *
 *  - `sharedAccessToken`: the access token in the storage every tab shares:
 *    a string, `null` when the storage was READ and holds none, `undefined`
 *    when it could not be read at all (no storage, or it threw). The two
 *    differ on purpose, see {@link sessionGuardHook}.
 *  - `persistenceUnavailable`: the AuthStore could not write the session to
 *    storage (`sessionPersistenceUnavailable`: a quota error or blocked site
 *    data). Storage then holds an older credential than the tab's own memory,
 *    so the refresh-token pre-send binding below has nothing true to compare
 *    with and is skipped.
 *  - `resumeConnection`: called before a call goes out that is the next
 *    session's, to bring back a connection the host tore down when it refused
 *    a foreign credential (RootStore.endLocalSession). A call that steps aside
 *    (superseded, held) never calls it.
 */
export type GuardLink = {
  sharedAccessToken?: () => string | null | undefined;
  persistenceUnavailable?: () => boolean;
  resumeConnection?: () => void;
};

type HookableApp = {
  hooks?: (hooks: { around: { all: AroundHook[] } }) => unknown;
};

/** Services whose calls belong to the sign-in/sign-out itself. */
const EXEMPT_PATHS = new Set(["authentication", "refresh-token"]);

/**
 * Is this auth call a refresh, spending the credential every tab shares? The
 * exchange itself, and the jwt `authentication.create` that presents the
 * access token it returned. An interactive (local) sign-in is not: the person
 * typed their credentials into THIS tab.
 */
const spendsSharedCredential = (context: GuardContext) =>
  context.path === "refresh-token" ||
  (context.data as { strategy?: unknown } | null | undefined)?.strategy === "jwt";

/**
 * Whose token this is: the `sub` claim of a JWT (HQ's access AND refresh
 * tokens are both JWTs — api/src/authentication/hooks/refreshToken.ts), as a
 * string, or undefined when it is not a JWT or carries no subject. Reads the
 * claim only; nothing here verifies a signature, and nothing needs to — it is
 * compared with who this tab already has signed in, to refuse a mismatch.
 */
export function tokenSubject(token: unknown): string | undefined {
  if (typeof token !== "string") return undefined;
  const parts = token.split(".");
  if (parts.length !== 3) return undefined;
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded)) as { sub?: unknown };
    return payload.sub == null || payload.sub === "" ? undefined : String(payload.sub);
  } catch {
    return undefined;
  }
}

/**
 * `499`, not 401/403: nothing treats it as an auth failure (no redirect to
 * login), and the offline library classifies a 4xx that is not a transient
 * code as a final server decision — so a write that fails with this is not
 * queued for replay.
 */
export const SESSION_ENDED_CODE = 499;

export class SessionEndedError extends Error {
  readonly name = "SessionEnded";
  readonly code: number = SESSION_ENDED_CODE;
  constructor(path: string) {
    super(`Signed out before ${path} loaded.`);
  }
}

/**
 * A sign-in or token refresh that was overtaken by a sign-out. `408`, not 499:
 * the offline library and `AuthStore.isAuthRejection` read a 4xx other than
 * 408/429 as "the server refused this credential" and clear the session, which
 * here would be the NEXT person's. A 408 is a timeout — keep the tokens, retry.
 */
export const AUTH_SUPERSEDED_CODE = 408;

export class AuthSupersededError extends SessionEndedError {
  readonly code = AUTH_SUPERSEDED_CODE;
  constructor(path: string) {
    super(path);
    this.message = `Signed out before ${path} completed.`;
  }
}

/**
 * A queued offline write whose owner is not the person signed in. `503` is in
 * the offline library's transient set: the queue keeps the op, charges no retry
 * and pauses the pass — the write is neither sent under the wrong identity nor
 * dropped nor duplicated.
 */
export class ReplayHeldError extends Error {
  readonly name = "ReplayHeld";
  readonly code = 503;
  constructor(path: string) {
    super(`A queued write to ${path} is held: it does not belong to the person signed in.`);
  }
}

/** True for the errors the guard raises on a session change; stores use it to stay quiet about them. */
export function isSessionEnded(error: unknown): error is SessionEndedError {
  if (typeof error !== "object" || error === null) return false;
  const { name, code } = error as { name?: unknown; code?: unknown };
  return name === "SessionEnded" && (code === SESSION_ENDED_CODE || code === AUTH_SUPERSEDED_CODE);
}

/**
 * Keep a superseded sign-in/refresh (see {@link AuthSupersededError}) off
 * `AuthStore.errorOnAuthenticate`. The library parks every failed attempt's
 * error there and a reaction turns it into a warning toast — here the NEXT
 * person would read "Signed out before refresh-token completed." on their
 * first screen, about a call that was never theirs.
 */
export function ignoreSessionEndedAuthErrors(authStore: unknown): () => void {
  if (typeof authStore !== "object" || authStore === null) return () => undefined;
  try {
    // MobX interception cancels the write before the library's reaction can see
    // it (`setErrorOnAuth` itself is a non-configurable action, so it cannot be
    // wrapped). Returning null drops the change.
    return intercept(
      authStore as object,
      "errorOnAuthenticate" as never,
      ((change: { newValue?: unknown }) =>
        isSessionEnded(change.newValue) ? null : change) as never,
    );
  } catch {
    // shortcut: an AuthStore without that observable shows the toast; ask the
    // library to ignore a superseded attempt itself (the session-safety handoff).
    return () => undefined;
  }
}

/** How a session ended, as {@link watchSessionEnds} saw it. */
export type SessionEndCause =
  | "logout-event"
  | "peer-sign-out"
  | "foreign-credential"
  | "tokens-cleared";

type EventSource = {
  on?: (event: string, listener: () => void) => unknown;
  off?: (event: string, listener: () => void) => unknown;
  removeListener?: (event: string, listener: () => void) => unknown;
};
type StorageEventTarget = {
  addEventListener?: (type: "storage", listener: (event: StorageEvent) => void) => void;
  removeEventListener?: (type: "storage", listener: (event: StorageEvent) => void) => void;
};

/**
 * Report, synchronously, the START of each way a session can end without
 * going through `AuthStore.logout()` — so the host can begin a new `authEpoch`
 * before the library's own teardown takes its first `await`:
 *
 *  - `"logout-event"`: the Feathers app's `logout` event. The library handles a
 *    server-initiated one in `handleAppLogoutEvent`, which clears the session
 *    and only then purges; a refresh settling in between used to keep the old
 *    epoch and have its rotated tokens and user adopted.
 *  - `"peer-sign-out"`: another tab removed the shared refresh token. The
 *    library mirrors it with `clearStore()` and nothing else: no purge, no new
 *    epoch, so a refresh in flight in THIS tab was adopted whenever it landed,
 *    and its rotated token written back into the storage every tab shares.
 *  - `"foreign-credential"`: another tab wrote a refresh or access token that
 *    is not the signed-in person's (its `sub` is someone else's, or it has
 *    none to read). The library adopts any new value as a sibling's rotation,
 *    so a refresh begun by the previous person in another tab, landing after
 *    the next person signed in, put the previous person's tokens under the
 *    next person's screen. This listener runs ahead of the library's and stops
 *    the event there (`stopImmediatePropagation`), so the library never adopts
 *    it, and the host ends this tab's session (fail closed: whoever it is signs
 *    in again). A token for the same person passes through — rotation in
 *    another tab keeps working — and so does any token while this tab has
 *    nobody signed in (`sessionOwner` undefined): there is no session to mix
 *    it into.
 *
 * MUST be called before the AuthStore is constructed: an EventEmitter and an
 * EventTarget call listeners in the order they were added, so these run ahead
 * of the library's own. (`AuthStore.logout()` — the local sign-out, and the
 * socket's `removeAuthTokens` and a removed user, which call it — starts with
 * `rootStore.flushOfflineQueue()`, which starts the epoch itself.)
 *
 * Returns a disposer that removes both listeners.
 */
export function watchSessionEnds(options: {
  app: unknown;
  /** The window (or a test double); omitted where there is none. */
  target?: unknown;
  refreshStorageKey: string;
  /** The access-token key; its non-empty writes are checked like the refresh key's. */
  authStorageKey?: string;
  /** Who this tab has signed in now (a user id), or undefined for nobody. */
  sessionOwner?: () => string | undefined;
  onEnd: (cause: SessionEndCause) => void;
  /**
   * A credential reached this tab while nobody was signed in here and was NOT
   * refused: the library is about to take it up, as it always has. The host
   * uses it to bring back a connection it tore down on a refused credential,
   * since the library signs in from a connect event. Called after the event
   * has reached the library's listener (a later task), so the AuthStore
   * already holds the new tokens when the connection comes back.
   */
  onAdopt?: () => void;
}): () => void {
  const disposers: Array<() => void> = [];
  const app = options.app as EventSource | null;
  if (typeof app?.on === "function") {
    const onLogout = () => options.onEnd("logout-event");
    app.on("logout", onLogout);
    disposers.push(() => {
      if (typeof app.off === "function") app.off("logout", onLogout);
      else app.removeListener?.("logout", onLogout);
    });
  }
  const target = options.target as StorageEventTarget | null | undefined;
  if (typeof target?.addEventListener === "function") {
    // Whose credential this tab last refused. Its companion write (the access
    // token after the refresh token, or the other way round) arrives once the
    // session has already ended here — nobody signed in — and must be refused
    // too, or the library adopts half of what was just turned away. Cleared
    // by a sign-out in another tab, or once this tab has a person again.
    let refusing: { subject: string | undefined } | null = null;
    const pending = new Set<ReturnType<typeof setTimeout>>();
    // A pending take-up must not outlive a session end that follows it in the
    // same turn: its resume would bring the torn-down connection back before
    // the purge the end started has finished.
    const cancelAdoptions = () => {
      pending.forEach((timer) => clearTimeout(timer));
      pending.clear();
    };
    const onStorage = (event: StorageEvent) => {
      // `storage.clear()` (key null) is ignored, as the library ignores it.
      if (!event.key) return;
      const isRefresh = event.key === options.refreshStorageKey;
      const isAccess = !!options.authStorageKey && event.key === options.authStorageKey;
      if (!isRefresh && !isAccess) return;
      if (!event.newValue) {
        // A removed refresh token is a sign-out. (A removed access token alone
        // is a sibling's teardown step; the refresh key follows it.)
        if (isRefresh) {
          refusing = null;
          cancelAdoptions();
          options.onEnd("peer-sign-out");
        }
        return;
      }
      const subject = tokenSubject(event.newValue);
      const owner = options.sessionOwner?.();
      if (owner === undefined) {
        if (refusing && refusing.subject === subject) {
          event.stopImmediatePropagation?.();
          return;
        }
        // Taking up a credential that was not refused: whoever was refused
        // earlier is history, and the next write of theirs is a fresh event.
        refusing = null;
        // After this event has been delivered to EVERY listener, not just
        // this one: the library's own `storage` listener (registered after
        // ours) is what loads the new tokens into the AuthStore, and the
        // connection the host brings back signs in from them. A macrotask,
        // not a microtask: the browser runs microtasks between the listeners
        // of an event it dispatches itself.
        const timer = setTimeout(() => {
          pending.delete(timer);
          options.onAdopt?.();
        }, 0);
        pending.add(timer);
        return;
      }
      refusing = null;
      if (subject === owner) return; // the same person's rotation
      // Not this tab's person: keep the library from adopting it, end the session.
      refusing = { subject };
      event.stopImmediatePropagation?.();
      cancelAdoptions();
      options.onEnd("foreign-credential");
    };
    target.addEventListener("storage", onStorage);
    disposers.push(() => {
      target.removeEventListener?.("storage", onStorage);
      cancelAdoptions();
    });
  }
  return () => disposers.splice(0).forEach((dispose) => dispose());
}

/**
 * Report the moment the AuthStore drops a session's credentials, whatever
 * dropped them: `clearStore()` runs at the start of every teardown the library
 * has (both logouts, a peer sign-out, a definitive refresh failure, dispose),
 * so this is the backstop for a path {@link watchSessionEnds} does not name —
 * including one a later library version adds. Fires whenever `token` or
 * `refreshToken` goes from a value to none (a teardown clearing both fires
 * twice; an extra epoch is harmless). Returns a disposer for the observers, or
 * null (and the host logs it) when the store has neither as an observable to
 * watch.
 */
export function watchTokensCleared(
  authStore: unknown,
  onCleared: () => void,
): (() => void) | null {
  if (typeof authStore !== "object" || authStore === null) return null;
  const disposers: Array<() => void> = [];
  for (const key of ["token", "refreshToken"]) {
    try {
      disposers.push(
        observe(authStore as object, key as never, ((change: { oldValue?: unknown; newValue?: unknown }) => {
          if (change.oldValue && !change.newValue) onCleared();
        }) as never),
      );
    } catch {
      // Not observable under that name: the caller reports it.
    }
  }
  if (!disposers.length) return null;
  return () => disposers.splice(0).forEach((dispose) => dispose());
}

/**
 * The around hook itself, for a given epoch source. Exported for tests.
 *
 * @param sharedCredential reads the refresh token in the storage every tab
 *   shares (null when there is none, or no storage) — the compare-and-swap a
 *   refresh is checked against (see the file comment).
 */
export function sessionGuardHook(
  epoch: () => number,
  isReplay: (call: GuardContext) => ReplayVerdict = () => false,
  authEpoch: () => number = epoch,
  sharedCredential?: () => string | null | undefined,
  link: GuardLink = {},
): AroundHook {
  // Tri-state: a string, `null` (the storage was READ and holds none: another
  // tab signed out), or `undefined` (it could not be read, or the host lends
  // no reader). Collapsing the last two lets a stale refresh through after a
  // peer's sign-out whose event this tab has not heard yet.
  const readShared = (): string | null | undefined => {
    try {
      return sharedCredential?.();
    } catch {
      return undefined;
    }
  };
  const readSharedAccess = (): string | null | undefined => {
    try {
      return link.sharedAccessToken?.();
    } catch {
      return undefined;
    }
  };
  const persistenceUnavailable = () => {
    try {
      return link.persistenceUnavailable?.() === true;
    } catch {
      return false;
    }
  };
  const resume = () => {
    try {
      link.resumeConnection?.();
    } catch {
      // A connection that will not come back must not fail the call itself.
    }
  };
  return async (context, next) => {
    if (EXEMPT_PATHS.has(context.path)) {
      // Only a sign-in / refresh / authenticate can adopt a person; the revoke
      // and logout the sign-out itself sends are not "adopting" anything.
      if (context.method !== "create") return next();
      const authStartedIn = authEpoch();
      const swaps = sharedCredential !== undefined && spendsSharedCredential(context);
      const heldBefore = swaps ? readShared() : undefined;
      const accessBefore = swaps ? readSharedAccess() : undefined;
      // A refresh that would spend a credential that is not the shared
      // storage's PERSON's was overtaken before it started (a tab that has not
      // yet heard another tab's sign-out and sign-in): it steps aside unsent,
      // so it can neither rotate nor write over the stored session.
      //
      // Compared by SUBJECT, not by the token string. A same-person rotation
      // changes the string, and when the library could not write the rotated
      // token to storage (a quota error: it keeps the new token in memory and
      // storage keeps the old one) every later refresh differs from storage
      // for a perfectly legitimate reason; refusing those sent the person back
      // to sign in once per access-token lifetime. So: refused when the two
      // subjects differ, or when only one side has a readable subject. Tokens
      // with no readable subject on EITHER side (not JWTs; nothing in HQ) fall
      // back to the exact comparison. Skipped while the library reports that
      // it cannot persist the session at all.
      //
      // Storage that was READ and is EMPTY (`null`) differs from every token a
      // tab can present: another tab signed out and this tab has not heard
      // yet (its storage event is withheld or delayed), so a refresh here
      // would rotate a session the person ended and write it back. Refused
      // unsent, like a mismatch. Storage that could not be read at all
      // (`undefined`) is not evidence of anything and keeps the old policy,
      // as does a library that cannot persist (storage is empty because it
      // was never written).
      const spending = (context.data as { refreshToken?: unknown } | null | undefined)?.refreshToken;
      if (swaps && context.path === "refresh-token" && typeof spending === "string" && !persistenceUnavailable()) {
        if (heldBefore === null) throw new AuthSupersededError(context.path);
        if (typeof heldBefore === "string" && heldBefore !== spending) {
          const stored = tokenSubject(heldBefore);
          const presented = tokenSubject(spending);
          if (stored !== presented || stored === undefined) throw new AuthSupersededError(context.path);
        }
      }
      // The jwt hop presents an ACCESS token; bind it to whoever the shared
      // storage belongs to (see the file comment). Only a known mismatch is
      // refused: with a token whose subject cannot be read there is nothing to
      // compare it with. Neither token stored (both READ and empty) is the
      // peer sign-out above, and refused the same way. Both checks are skipped
      // while the library cannot persist the session, like the refresh-token
      // hop's: storage then holds an OLDER credential than this tab's memory,
      // possibly another person's, so a mismatch proves nothing - and refusing
      // here, after the refresh-token hop was let through and rotated the
      // token on the server, would lose that rotation.
      if (swaps && context.path === "authentication" && !persistenceUnavailable()) {
        const presented = tokenSubject((context.data as { accessToken?: unknown } | null | undefined)?.accessToken);
        const stored = tokenSubject(heldBefore) ?? tokenSubject(accessBefore);
        if (presented !== undefined && stored !== undefined && presented !== stored) {
          throw new AuthSupersededError(context.path);
        }
        if (heldBefore === null && accessBefore === null) {
          throw new AuthSupersededError(context.path);
        }
      }
      resume();
      // Settle-time compare-and-swap. The refresh token anchors it; with none
      // to anchor on (nothing stored, or unreadable) the access token does.
      const superseded = () =>
        authEpoch() !== authStartedIn ||
        (swaps &&
          (readShared() !== heldBefore ||
            (typeof heldBefore !== "string" && readSharedAccess() !== accessBefore)));
      try {
        await next();
      } catch (error) {
        if (!superseded()) throw error;
        throw new AuthSupersededError(context.path);
      }
      if (!superseded()) return;
      context.result = undefined;
      throw new AuthSupersededError(context.path);
    }
    const replay = isReplay(context);
    if (replay === "refuse") throw new ReplayHeldError(context.path);
    resume();
    if (replay) return next();
    const startedIn = epoch();
    try {
      await next();
    } catch (error) {
      if (epoch() === startedIn) throw error;
      // The call failed after the sign-out; the failure is the old session's.
      throw new SessionEndedError(context.path);
    }
    if (epoch() === startedIn) return;
    // Drop the answer with the session it belongs to.
    context.result = undefined;
    throw new SessionEndedError(context.path);
  };
}

/**
 * Install the guard on a Feathers client app. A test double without app-level
 * hooks is left alone.
 */
export function installSessionGuard(
  app: unknown,
  epoch: () => number,
  isReplay?: (call: GuardContext) => ReplayVerdict,
  authEpoch?: () => number,
  sharedCredential?: () => string | null | undefined,
  link?: GuardLink,
): void {
  const hookable = app as HookableApp | null;
  if (typeof hookable?.hooks !== "function") return;
  hookable.hooks({
    around: { all: [sessionGuardHook(epoch, isReplay, authEpoch, sharedCredential, link)] },
  });
}

/**
 * A marker taken where a load starts: `ended()` is true once a sign-out has
 * purged the stores since. A view store checks it after each `await` before it
 * writes state, latches a `loaded` flag, or starts a timer or subscription —
 * the guard has already made the call itself reject, but a loader that swallows
 * errors (a library ServiceStore returns `undefined`/`null`) or keeps its own
 * `catch` would otherwise still run its tail over a store `reset()` emptied.
 *
 *     const ended = sessionMark(this.rootStore);
 *     await this.service.find(...);
 *     if (ended()) return;
 */
export function sessionMark(root: unknown): () => boolean {
  const read = () => (root as { sessionEpoch?: number } | null | undefined)?.sessionEpoch ?? 0;
  const startedIn = read();
  return () => read() !== startedIn;
}
